<template>
  <div class="navigation-container">
    <!-- Mobile menu button -->
    <div class="lg:hidden fixed top-4 left-4 z-50">
      <button
        class="p-2 rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700"
        @click="toggleMobileMenu"
      >
        <svg
          class="w-6 h-6 text-gray-600 dark:text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>
    </div>

    <!-- Mobile overlay -->
    <div v-if="mobileMenuOpen" class="fixed inset-0 z-40 lg:hidden" @click="closeMobileMenu">
      <div class="fixed inset-0 bg-gray-600 bg-opacity-75" />
    </div>

    <!-- Sidebar -->
    <nav
      class="fixed top-0 left-0 z-50 flex flex-col w-64 h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out lg:translate-x-0"
      :class="{
        'translate-x-0': mobileMenuOpen,
        '-translate-x-full': !mobileMenuOpen,
      }"
    >
      <!-- Logo and Brand -->
      <div
        class="flex items-center justify-between h-16 px-6 border-b border-gray-200 dark:border-gray-700"
      >
        <div class="flex items-center">
          <svg
            class="w-8 h-8 text-primary-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
          <span class="ml-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
            MCP Tools
          </span>
        </div>
        <!-- Mobile close button -->
        <button
          class="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          @click="closeMobileMenu"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Navigation Menu -->
      <div class="flex-1 px-4 py-6 overflow-y-auto">
        <nav class="space-y-2">
          <!-- Dashboard -->
          <RouterLink
            to="/"
            class="nav-item"
            active-class="active"
            exact
          >
            <svg
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6a2 2 0 01-2 2H10a2 2 0 01-2-2V5z"
              />
            </svg>
            Dashboard
          </RouterLink>

          <!-- Tools -->
          <RouterLink to="/tools" class="nav-item" active-class="active">
            <svg
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
              />
            </svg>
            Tools
            <span
              v-if="availableToolsCount > 0"
              class="ml-auto bg-primary-100 text-primary-800 text-xs font-medium px-2 py-1 rounded-full dark:bg-primary-900 dark:text-primary-200"
            >
              {{ availableToolsCount }}
            </span>
          </RouterLink>

          <!-- Conversations -->
          <RouterLink to="/conversations" class="nav-item" active-class="active">
            <svg
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            Conversations
          </RouterLink>

          <!-- Categories -->
          <div class="pt-4">
            <h3
              class="px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Categories
            </h3>
            <div class="mt-2 space-y-1">
              <RouterLink
                to="/tools?category=search"
                class="nav-item text-sm"
                active-class="active"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                Search Tools
              </RouterLink>

              <RouterLink
                to="/tools?category=calculation"
                class="nav-item text-sm"
                active-class="active"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                Calculator
              </RouterLink>

              <RouterLink
                to="/tools?category=communication"
                class="nav-item text-sm"
                active-class="active"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
                Communication
              </RouterLink>

              <RouterLink
                to="/tools?category=document"
                class="nav-item text-sm"
                active-class="active"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Documents
              </RouterLink>
            </div>
          </div>

          <!-- User Tier Section -->
          <div class="pt-4">
            <h3
              class="px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Account Tier
            </h3>
            <div class="mt-2 px-3">
              <!-- Tier Badge -->
              <div
                class="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600"
              >
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center">
                    <span class="text-lg mr-2">{{ tierInfo.icon }}</span>
                    <div>
                      <div class="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Tier {{ userTier }}
                      </div>
                      <div class="text-xs text-gray-500 dark:text-gray-400">
                        {{ tierInfo.name }}
                      </div>
                    </div>
                  </div>
                  <button
                    class="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                    @click="showTierDropdown = !showTierDropdown"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>

                <!-- Progress Bar -->
                <div class="mb-2">
                  <div class="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                    <span>Progress</span>
                    <span>{{ tierProgress }}%</span>
                  </div>
                  <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div
                      class="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      :style="{ width: `${tierProgress}%` }"
                    />
                  </div>
                </div>

                <!-- Next Tier Info -->
                <div v-if="nextTierInfo" class="text-xs text-gray-500 dark:text-gray-400">
                  Next: {{ nextTierInfo.name }}
                </div>
              </div>

              <!-- Tier Dropdown -->
              <div
                v-if="showTierDropdown"
                class="mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2"
              >
                <!-- Quick Verification Actions -->
                <div class="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                  <div class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                    QUICK ACTIONS
                  </div>

                  <button
                    v-if="canVerifyEmail"
                    class="flex items-center w-full px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                    @click="
                      showEmailVerificationModal = true;
                      showTierDropdown = false;
                    "
                  >
                    <svg
                      class="w-3 h-3 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    Verify Email
                  </button>

                  <button
                    v-if="canVerifyPhone"
                    class="flex items-center w-full px-2 py-1 text-xs text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded mt-1"
                    @click="
                      showPhoneVerificationModal = true;
                      showTierDropdown = false;
                    "
                  >
                    <svg
                      class="w-3 h-3 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    Verify Phone
                  </button>
                </div>

                <!-- Tier Progress -->
                <div class="px-3 py-2">
                  <div class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                    TIER PROGRESS
                  </div>
                  <div v-if="nextTierInfo" class="text-xs text-gray-600 dark:text-gray-300">
                    <strong>Next:</strong> {{ nextTierInfo.name }}
                    <br />
                    <span class="text-gray-500 dark:text-gray-400">{{
                      nextTierInfo.requirements
                    }}</span>
                  </div>
                  <div v-else class="text-xs text-green-600 dark:text-green-400">
                    🎉 Maximum tier reached!
                  </div>
                </div>

                <div class="border-t border-gray-200 dark:border-gray-700 mt-2">
                  <RouterLink
                    to="/profile/tier"
                    class="flex items-center px-3 py-2 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    @click="showTierDropdown = false"
                  >
                    <svg
                      class="w-3 h-3 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                    View Tier Details
                  </RouterLink>
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Tools -->
          <div v-if="recentTools.length > 0" class="pt-4">
            <h3
              class="px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Recent
            </h3>
            <div class="mt-2 space-y-1">
              <RouterLink
                v-for="tool in recentTools"
                :key="tool.name"
                :to="`/tools/${tool.name}`"
                class="nav-item text-sm"
                active-class="active"
              >
                <div class="w-4 h-4 rounded bg-gray-300 dark:bg-gray-600" />
                {{ formatToolName(tool.name) }}
              </RouterLink>
            </div>
          </div>
        </nav>
      </div>

      <!-- User Profile Section -->
      <div class="p-4 border-t border-gray-200 dark:border-gray-700">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <img
              v-if="userProfile?.photoUrl"
              :src="userProfile.photoUrl"
              :alt="userDisplayName"
              class="w-8 h-8 rounded-full"
            />
            <div
              v-else
              class="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center"
            >
              <span class="text-sm font-medium text-white">
                {{ userInitials }}
              </span>
            </div>
          </div>
          <div class="ml-3 flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {{ userDisplayName }}
            </p>
            <div class="flex items-center">
              <p class="text-xs text-gray-500 dark:text-gray-400 truncate">
                @{{ userProfile?.username || 'user' }}
              </p>
              <span
                class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
              >
                T{{ userTier }}
              </span>
            </div>
          </div>
          <div class="ml-2">
            <button
              class="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              @click="showUserMenu = !showUserMenu"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </button>
          </div>
        </div>

        <!-- User Dropdown Menu -->
        <div
          v-if="showUserMenu"
          class="absolute bottom-16 left-4 right-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-10"
        >
          <RouterLink
            to="/profile"
            class="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            @click="showUserMenu = false"
          >
            <svg
              class="w-4 h-4 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            Profile Settings
          </RouterLink>

          <RouterLink
            to="/profile/tier"
            class="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            @click="showUserMenu = false"
          >
            <svg
              class="w-4 h-4 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            Tier Management
          </RouterLink>

          <div class="border-t border-gray-200 dark:border-gray-700 my-2" />

          <button
            v-if="canVerifyEmail"
            class="flex items-center w-full px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            @click="
              showEmailVerificationModal = true;
              showUserMenu = false;
            "
          >
            <svg
              class="w-4 h-4 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            Verify Email
          </button>

          <button
            v-if="canVerifyPhone"
            class="flex items-center w-full px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            @click="
              showPhoneVerificationModal = true;
              showUserMenu = false;
            "
          >
            <svg
              class="w-4 h-4 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            Verify Phone
          </button>

          <button
            class="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            @click="toggleTheme"
          >
            <svg
              v-if="currentTheme === 'light'"
              class="w-4 h-4 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
            <svg
              v-else
              class="w-4 h-4 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            {{ currentTheme === 'light' ? 'Dark Mode' : 'Light Mode' }}
          </button>

          <div class="border-t border-gray-200 dark:border-gray-700 my-2" />

          <button
            class="flex items-center w-full px-4 py-2 text-sm text-error-600 dark:text-error-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            @click="handleLogout"
          >
            <svg
              class="w-4 h-4 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Sign Out
          </button>
        </div>
      </div>
    </nav>

    <!-- Verification Modals -->
    <EmailVerificationModal
      :is-open="showEmailVerificationModal"
      @close="showEmailVerificationModal = false"
      @verified="handleEmailVerified"
    />

    <PhoneVerificationModal
      :is-open="showPhoneVerificationModal"
      @close="showPhoneVerificationModal = false"
      @verified="handlePhoneVerified"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { storageService } from '@/services/storage';
import { mcpApi } from '@/services/mcp-api';
import EmailVerificationModal from '@/components/EmailVerificationModal.vue';
import PhoneVerificationModal from '@/components/PhoneVerificationModal.vue';

defineOptions({
  name: 'AppNavigation',
});

const router = useRouter();
const authStore = useAuthStore();

// State
const mobileMenuOpen = ref(false);
const showUserMenu = ref(false);
const availableToolsCount = ref(0);
const recentTools = ref<any[]>([]);
const currentTheme = ref(storageService.getTheme());
const showTierDropdown = ref(false);
const showEmailVerificationModal = ref(false);
const showPhoneVerificationModal = ref(false);

// Computed
const userProfile = computed(() => authStore.userProfile);

const userTier = computed(() => authStore.user?.tier || 0);

const tierInfo = computed(() => {
  const tiers = [
    { level: 0, name: 'Basic Access', color: 'gray', icon: '👋' },
    { level: 1, name: 'Verified Contact', color: 'blue', icon: '📧' },
    { level: 2, name: 'Personalized', color: 'green', icon: '⚙️' },
    { level: 3, name: 'Multi-Bot User', color: 'purple', icon: '🤖' },
    { level: 4, name: 'Identity Verified', color: 'indigo', icon: '🆔' },
    { level: 5, name: 'Payment Ready', color: 'pink', icon: '💳' },
    { level: 6, name: 'Proven Spender', color: 'yellow', icon: '💰' },
    { level: 7, name: 'Enterprise', color: 'red', icon: '🏢' },
  ];
  return tiers[userTier.value] || tiers[0];
});

const nextTierInfo = computed(() => {
  const tiers = [
    { level: 1, name: 'Verified Contact', requirements: 'Email + Phone verification' },
    { level: 2, name: 'Personalized', requirements: 'Set preferences' },
    { level: 3, name: 'Multi-Bot User', requirements: '3+ active bots' },
    { level: 4, name: 'Identity Verified', requirements: 'ID/Passport verification' },
    { level: 5, name: 'Payment Ready', requirements: 'Add payment method' },
    { level: 6, name: 'Proven Spender', requirements: '$100+ spent' },
    { level: 7, name: 'Enterprise', requirements: '$500+ spent' },
  ];
  return tiers.find(t => t.level > userTier.value);
});

const tierProgress = computed(() => {
  const current = userTier.value;
  const max = 7;
  return Math.round((current / max) * 100);
});

const canVerifyEmail = computed(() => {
  return userTier.value === 0 && !authStore.user?.emailVerified;
});

const canVerifyPhone = computed(() => {
  return userTier.value <= 1 && !authStore.user?.phoneVerified;
});

const userDisplayName = computed(() => {
  if (!userProfile.value) return 'User';

  if (userProfile.value.firstName && userProfile.value.lastName) {
    return `${userProfile.value.firstName} ${userProfile.value.lastName}`;
  }

  return userProfile.value.firstName || userProfile.value.username || 'User';
});

const userInitials = computed(() => {
  if (!userProfile.value) return 'U';

  const firstName = userProfile.value.firstName || '';
  const lastName = userProfile.value.lastName || '';

  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }

  if (firstName) {
    return firstName[0].toUpperCase();
  }

  if (userProfile.value.username) {
    return userProfile.value.username[0].toUpperCase();
  }

  return 'U';
});

// Methods
const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

const closeMobileMenu = () => {
  mobileMenuOpen.value = false;
  showUserMenu.value = false;
};

const toggleTheme = () => {
  const newTheme = currentTheme.value === 'light' ? 'dark' : 'light';
  currentTheme.value = newTheme;
  storageService.setTheme(newTheme);

  // Apply theme immediately
  document.documentElement.classList.toggle('dark', newTheme === 'dark');

  showUserMenu.value = false;
};

const handleLogout = async () => {
  showUserMenu.value = false;

  try {
    await authStore.logout();
    await router.push({ name: 'auth' });
  } catch (error) {
    console.error('Logout error:', error);
  }
};

const formatToolName = (toolName: string): string => {
  return toolName.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const loadToolsData = async () => {
  try {
    // Load available tools count
    const registry = await mcpApi.getToolRegistry();
    availableToolsCount.value = Object.keys(registry.tools || {}).length;

    // Load recent tools from user usage
    const usage = await mcpApi.getUserToolUsage();
    recentTools.value = usage
      .sort((a, b) => new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime())
      .slice(0, 5)
      .map(item => ({ name: item.toolName }));
  } catch (error) {
    console.error('Failed to load tools data:', error);
  }
};

// Handle verification success
const handleEmailVerified = async (email: string) => {
  showEmailVerificationModal.value = false;

  // Refresh user data to get updated tier
  try {
    await authStore.refreshUserData();

    // Show success notification
    console.log('Email verified successfully:', email);
  } catch (error) {
    console.error('Failed to refresh user data:', error);
  }
};

const handlePhoneVerified = async (phoneNumber: string) => {
  showPhoneVerificationModal.value = false;

  // Refresh user data to get updated tier
  try {
    await authStore.refreshUserData();

    // Show success notification
    console.log('Phone verified successfully:', phoneNumber);
  } catch (error) {
    console.error('Failed to refresh user data:', error);
  }
};

// Handle clicks outside to close menus
const handleClickOutside = (event: Event) => {
  const target = event.target as Element;

  if (!target.closest('.navigation-container')) {
    showUserMenu.value = false;
    showTierDropdown.value = false;
  }
};

// Lifecycle
onMounted(async () => {
  await loadToolsData();
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.navigation-container {
  position: relative;
}

.nav-item {
  @apply flex items-center px-3 py-2 text-sm font-medium rounded-lg;
  @apply text-gray-700 hover:bg-gray-100 hover:text-gray-900;
  @apply dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100;
  @apply transition-colors duration-150;
}

.nav-item svg {
  @apply mr-3 flex-shrink-0;
}

.nav-item.active {
  @apply bg-primary-100 text-primary-700;
  @apply dark:bg-primary-900 dark:text-primary-300;
}

.nav-item:focus {
  @apply outline-none ring-2 ring-primary-500 ring-offset-2;
  @apply dark:ring-offset-gray-800;
}

/* Mobile menu animations */
@media (max-width: 1023px) {
  .navigation-container nav {
    box-shadow:
      0 10px 25px -5px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
}

/* Smooth scrollbar */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: theme('colors.gray.300') transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: theme('colors.gray.300');
  border-radius: 2px;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: theme('colors.gray.600');
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .nav-item,
  nav {
    transition: none;
  }
}

/* Print styles */
@media print {
  .navigation-container {
    display: none;
  }
}
</style>
